import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useContent } from "../../hooks/useContent";

const defaultProjectTypes = ["Web Development", "Mobile App", "UI/UX Design", "Branding", "E-commerce", "Other"];

const QuoteModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        project_type: "",
        budget: "",
        details: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [currency, setCurrency] = useState({ code: "USD", symbol: "$" });
    const [budgetRanges, setBudgetRanges] = useState([
        "< $5k", "$5k - $10k", "$10k - $25k", "$25k - $50k", "$50k+"
    ]);
    const [projectTypes, setProjectTypes] = useState(defaultProjectTypes);
    const [settings, setSettings] = useState({ title: "", success_message: "" });
    const { content: quoteSettings } = useContent("quote_settings");

    useEffect(() => {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (timeZone.includes("Calcutta") || timeZone.includes("Kolkata") || timeZone.includes("India")) {
            setCurrency({ code: "INR", symbol: "₹" });
            setBudgetRanges([
                "< ₹4 Lakhs", "₹4L - ₹8L", "₹8L - ₹20L", "₹20L - ₹40L", "₹40 Lakhs+"
            ]);
        } else if (timeZone.includes("London") || timeZone.includes("Europe/London")) {
            setCurrency({ code: "GBP", symbol: "£" });
            setBudgetRanges([
                "< £4k", "£4k - £8k", "£8k - £20k", "£20k - £40k", "£40k+"
            ]);
        } else if (timeZone.includes("Europe")) {
            setCurrency({ code: "EUR", symbol: "€" });
            setBudgetRanges([
                "< €5k", "€5k - €10k", "€10k - €25k", "€25k - €50k", "€50k+"
            ]);
        } else {
            setCurrency({ code: "USD", symbol: "$" });
            setBudgetRanges([
                "< $5k", "$5k - $10k", "$10k - $25k", "$25k - $50k", "$50k+"
            ]);
        }
    }, []);

    useEffect(() => {
        if (!quoteSettings) return;
        setSettings(quoteSettings);
        if (quoteSettings.project_types?.length > 0) {
            setProjectTypes(quoteSettings.project_types);
        }
        if (quoteSettings.budget_ranges?.length > 0) {
            setBudgetRanges(quoteSettings.budget_ranges);
        }
    }, [quoteSettings]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSelect = (name, value) => {
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Final Validation
        if (!formData.details || formData.details.trim().length < 10) {
            toast.error("Please provide some more details about your project.", {
                style: { background: "#ff4444", color: "white" }
            });
            return;
        }

        if (!formData.project_type || !formData.budget) {
            toast.error("Please ensure all fields are selected.");
            return;
        }

        setIsSubmitting(true);
        try {
            await api.post("/quote", {
                ...formData,
                currency: currency.code
            });
            setIsSuccess(true);
            setTimeout(() => {
                onClose();
                setIsSuccess(false);
                setStep(1);
                setFormData({ name: "", email: "", company: "", project_type: "", budget: "", details: "" });
            }, 3000);
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.response?.status === 429
                ? "Too many requests. Please wait a few minutes."
                : "Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const nextStep = () => {
        if (step === 1) {
            if (!formData.name || !formData.email) {
                toast.error("Please fill in your contact details.");
                return;
            }
        }
        if (step === 2) {
            if (!formData.project_type || !formData.budget) {
                toast.error("Please select a project type and budget.");
                return;
            }
        }
        setStep(step + 1);
    };

    const prevStep = () => setStep(step - 1);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-xl p-0 md:p-6"
                >
                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        className="bg-neutral-900 border border-white/10 w-full max-w-2xl relative overflow-hidden flex flex-col h-full md:h-auto md:max-h-[90vh] rounded-none md:rounded-lg"
                    >
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors z-20 p-2"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Progress Bar */}
                        <div className="h-1 bg-white/5 w-full absolute top-0 left-0">
                            <motion.div
                                className="h-full bg-accent"
                                initial={{ width: "0%" }}
                                animate={{ width: isSuccess ? "100%" : `${(step / 3) * 100}%` }}
                                transition={{ duration: 0.5 }}
                            />
                        </div>

                        {/* Content */}
                        <div className="p-6 md:p-12 overflow-y-auto custom-scrollbar flex-1">
                            {isSuccess ? (
                                <div className="flex flex-col items-center justify-center py-12 text-center h-full">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                                    >
                                        <CheckCircle className="w-16 h-16 md:w-20 md:h-20 text-accent mb-6" />
                                    </motion.div>
                                    <h3 className="text-2xl md:text-3xl font-display font-bold text-white mb-2">Request Received</h3>
                                    <p className="text-gray-400 text-sm md:text-base">{settings.success_message || "We'll analyze your requirements and get back to you within 24 hours."}</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8 flex flex-col justify-center h-full">
                                    <div className="mb-2 md:mb-8">
                                        <span className="text-accent font-mono text-xs md:text-sm uppercase tracking-widest">Step 0{step}/03</span>
                                        <h2 className="text-2xl md:text-4xl font-display font-bold text-white mt-2">
                                            {step === 1 && (settings.title || "Let's start with the basics.")}
                                            {step === 2 && "Tell us about the project."}
                                            {step === 3 && "Final details."}
                                        </h2>
                                    </div>

                                    {step === 1 && (
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="space-y-4 md:space-y-6"
                                        >
                                            <div className="group">
                                                <label className="block text-gray-500 text-[10px] md:text-xs uppercase tracking-widest mb-2">Full Name *</label>
                                                <input
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className="w-full bg-black/50 border border-white/10 p-3 md:p-4 text-white focus:border-accent outline-none transition-colors text-sm md:text-base rounded-none appearance-none"
                                                    placeholder="John Doe"
                                                />
                                            </div>
                                            <div className="group">
                                                <label className="block text-gray-500 text-[10px] md:text-xs uppercase tracking-widest mb-2">Email Address *</label>
                                                <input
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    type="email"
                                                    className="w-full bg-black/50 border border-white/10 p-3 md:p-4 text-white focus:border-accent outline-none transition-colors text-sm md:text-base rounded-none appearance-none"
                                                    placeholder="john@company.com"
                                                />
                                            </div>
                                            <div className="group">
                                                <label className="block text-gray-500 text-[10px] md:text-xs uppercase tracking-widest mb-2">Company (Optional)</label>
                                                <input
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleChange}
                                                    className="w-full bg-black/50 border border-white/10 p-3 md:p-4 text-white focus:border-accent outline-none transition-colors text-sm md:text-base rounded-none appearance-none"
                                                    placeholder="Acme Inc."
                                                />
                                            </div>
                                        </motion.div>
                                    )}

                                    {step === 2 && (
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="space-y-6 md:space-y-8"
                                        >
                                            <div>
                                                <label className="block text-gray-500 text-[10px] md:text-xs uppercase tracking-widest mb-3 md:mb-4">Project Type</label>
                                                <div className="grid grid-cols-2 gap-2 md:gap-3">
                                                    {projectTypes.map((type) => (
                                                        <button
                                                            key={type}
                                                            type="button"
                                                            onClick={() => handleSelect("project_type", type)}
                                                            className={`p-3 text-xs md:text-sm border text-left transition-all ${formData.project_type === type
                                                                ? "border-accent bg-accent/10 text-accent"
                                                                : "border-white/10 text-gray-400 hover:border-white/30"
                                                                }`}
                                                        >
                                                            {type}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-gray-500 text-[10px] md:text-xs uppercase tracking-widest mb-3 md:mb-4">Budget Range ({currency.code})</label>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                                                    {budgetRanges.map((range) => (
                                                        <button
                                                            key={range}
                                                            type="button"
                                                            onClick={() => handleSelect("budget", range)}
                                                            className={`p-3 text-xs md:text-sm border text-center transition-all ${formData.budget === range
                                                                ? "border-accent bg-accent/10 text-accent"
                                                                : "border-white/10 text-gray-400 hover:border-white/30"
                                                                }`}
                                                        >
                                                            {range}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {step === 3 && (
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="space-y-4 md:space-y-6"
                                        >
                                            <div className="group">
                                                <label className="block text-gray-500 text-[10px] md:text-xs uppercase tracking-widest mb-2">Project Details *</label>
                                                <textarea
                                                    name="details"
                                                    value={formData.details}
                                                    onChange={handleChange}
                                                    rows="6"
                                                    required
                                                    className="w-full bg-black/50 border border-white/10 p-3 md:p-4 text-white focus:border-accent outline-none transition-colors resize-none text-sm md:text-base rounded-none appearance-none"
                                                    placeholder="Tell us about your goals, timeline, and any specific requirements..."
                                                />
                                            </div>
                                        </motion.div>
                                    )}

                                    <div className="flex justify-between pt-6 md:pt-8 border-t border-white/5 mt-auto md:mt-8">
                                        {step > 1 ? (
                                            <button
                                                type="button"
                                                onClick={prevStep}
                                                className="text-gray-500 hover:text-white transition-colors text-xs md:text-sm font-mono uppercase tracking-widest"
                                            >
                                                Back
                                            </button>
                                        ) : <div></div>}

                                        {step < 3 ? (
                                            <button
                                                type="button"
                                                onClick={nextStep}
                                                className="bg-white hover:bg-accent text-black px-6 md:px-8 py-2 md:py-3 font-display font-bold uppercase tracking-widest text-xs md:text-sm transition-colors"
                                            >
                                                Next
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="bg-accent hover:bg-white text-black px-6 md:px-8 py-2 md:py-3 font-display font-bold uppercase tracking-widest text-xs md:text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                                            >
                                                {isSubmitting ? <Loader2 className="animate-spin w-4 h-4" /> : "Submit Request"}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default QuoteModal;
